// @flow
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputBase from '@mui/material/InputBase';

import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import WrapTextIcon from '@mui/icons-material/WrapText';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import { fetchFileBuffer, downloadFileBlob } from './fileViewerUtils';

const ENCODING_OPTIONS = [
  { value: 'auto', label: 'Auto Detect' },
  { value: 'utf-8', label: 'UTF-8' },
  { value: 'gb18030', label: 'GB18030 (Simplified Chinese / 简体中文)' },
  { value: 'big5', label: 'Big5 (Traditional Chinese / 繁體中文)' },
  { value: 'windows-1252', label: 'Windows-1252 (Western European)' },
  { value: 'utf-16le', label: 'UTF-16LE' },
];

/**
 * Robust text decoder supporting automatic detection for UTF-8, Chinese, and ANSI text.
 */
function decodeTextBytes(bytes, requestedEncoding) {
  if (!bytes || bytes.length === 0) {
    return { text: '', detectedEncoding: 'utf-8' };
  }

  // 1. Detect Byte Order Mark (BOM)
  if (bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
    const sub = bytes.subarray(3);
    return { text: new TextDecoder('utf-8').decode(sub), detectedEncoding: 'UTF-8 (BOM)' };
  }
  if (bytes.length >= 2 && bytes[0] === 0xFF && bytes[1] === 0xFE) {
    const sub = bytes.subarray(2);
    return { text: new TextDecoder('utf-16le').decode(sub), detectedEncoding: 'UTF-16LE (BOM)' };
  }
  if (bytes.length >= 2 && bytes[0] === 0xFE && bytes[1] === 0xFF) {
    const sub = bytes.subarray(2);
    return { text: new TextDecoder('utf-16be').decode(sub), detectedEncoding: 'UTF-16BE (BOM)' };
  }

  // Explicit encoding selected by user
  if (requestedEncoding && requestedEncoding !== 'auto') {
    try {
      const decoder = new TextDecoder(requestedEncoding, { fatal: false });
      return { text: decoder.decode(bytes), detectedEncoding: requestedEncoding.toUpperCase() };
    } catch (e) {
      // Fallback
    }
  }

  // Auto-detection:
  // Step A: Try strict UTF-8
  try {
    const utf8Decoder = new TextDecoder('utf-8', { fatal: true });
    const text = utf8Decoder.decode(bytes);
    return { text, detectedEncoding: 'UTF-8' };
  } catch (err) {
    // UTF-8 failed, likely GB18030/GBK or Big5 or Windows-1252
  }

  // Step B: Try GB18030 (covers GB2312, GBK, GB18030 Simplified Chinese)
  try {
    const gbDecoder = new TextDecoder('gb18030', { fatal: true });
    const text = gbDecoder.decode(bytes);
    return { text, detectedEncoding: 'GB18030' };
  } catch (err) {
    // GB18030 failed
  }

  // Step C: Try Big5 (Traditional Chinese)
  try {
    const big5Decoder = new TextDecoder('big5', { fatal: true });
    const text = big5Decoder.decode(bytes);
    return { text, detectedEncoding: 'Big5' };
  } catch (err) {
    // Big5 failed
  }

  // Step D: Fallback to non-fatal UTF-8 with replacement characters
  const fallbackDecoder = new TextDecoder('utf-8', { fatal: false });
  return { text: fallbackDecoder.decode(bytes), detectedEncoding: 'UTF-8 (fallback)' };
}

export default function TextViewer(props) {
  const { fileUrl, filename, canDownload } = props;

  const [rawBytes, setRawBytes] = useState(null);
  const [fileBlob, setFileBlob] = useState(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [encoding, setEncoding] = useState('auto');
  const [detectedEncoding, setDetectedEncoding] = useState('UTF-8');
  const [wordWrap, setWordWrap] = useState(true);
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  const [fontSize, setFontSize] = useState(14);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [copied, setCopied] = useState(false);

  const textContainerRef = useRef(null);

  // Fetch file raw bytes
  const loadFile = useCallback(async () => {
    if (!fileUrl) return;

    setLoading(true);
    setError(null);

    try {
      const { arrayBuffer, blob } = await fetchFileBuffer(fileUrl);
      const u8 = new Uint8Array(arrayBuffer);
      setRawBytes(u8);
      setFileBlob(blob);

      const decoded = decodeTextBytes(u8, encoding);
      setText(decoded.text);
      setDetectedEncoding(decoded.detectedEncoding);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load text file:', err);
      setError(err);
      setLoading(false);
    }
  }, [fileUrl, encoding]);

  useEffect(() => {
    loadFile();
  }, [loadFile]);

  // Re-decode when user changes encoding dropdown
  const handleEncodingChange = (newEnc) => {
    setEncoding(newEnc);
    if (rawBytes) {
      const decoded = decodeTextBytes(rawBytes, newEnc);
      setText(decoded.text);
      setDetectedEncoding(decoded.detectedEncoding);
    }
  };

  // Zoom controls
  const handleZoomIn = () => {
    setFontSize((prev) => Math.min(26, prev + 2));
  };

  const handleZoomOut = () => {
    setFontSize((prev) => Math.max(10, prev - 2));
  };

  const handleZoomReset = () => {
    setFontSize(14);
  };

  // Copy to clipboard
  const handleCopy = () => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Print document
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${filename || 'Text Document'}</title>
          <style>
            body {
              font-family: 'Consolas', 'Courier New', monospace;
              font-size: 12px;
              line-height: 1.5;
              padding: 20px;
              color: #000;
              background: #fff;
              white-space: pre-wrap;
              word-break: break-all;
            }
          </style>
        </head>
        <body>${escaped}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // Download file
  const handleDownload = () => {
    if (fileBlob) {
      downloadFileBlob(fileBlob, filename || 'document.txt');
    } else if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };

  // Split lines
  const lines = useMemo(() => {
    return text ? text.split(/\r?\n/) : [];
  }, [text]);

  const stats = useMemo(() => {
    const charCount = text ? text.length : 0;
    const lineCount = lines.length;
    return { charCount, lineCount };
  }, [text, lines]);

  // Color schemes
  const bgTheme = isDarkMode ? '#1e1e1e' : '#fafafa';
  const textTheme = isDarkMode ? '#d4d4d4' : '#1f2937';
  const gutterBg = isDarkMode ? '#252526' : '#f3f4f6';
  const gutterColor = isDarkMode ? '#858585' : '#9ca3af';
  const borderColor = isDarkMode ? '#333333' : '#e5e7eb';
  const searchHighlightBg = isDarkMode ? '#f59e0b' : '#fde047';
  const searchHighlightColor = '#000000';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        bgcolor: bgTheme,
        color: textTheme,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Toolbar */}
      <Paper
        elevation={2}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 0.75,
          bgcolor: '#323639',
          color: '#ffffff',
          borderRadius: 0,
          zIndex: 10,
          flexShrink: 0,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {/* Left Toolbar Stats & Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="body2" sx={{ color: '#d1d5db', fontWeight: 600, fontSize: '0.85rem' }}>
            Text Viewer
          </Typography>
          {!loading && !error && (
            <Typography
              variant="caption"
              sx={{
                color: '#9ca3af',
                bgcolor: '#1f2937',
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontFamily: 'monospace',
              }}
            >
              {stats.lineCount} lines · {stats.charCount.toLocaleString()} chars · {detectedEncoding}
            </Typography>
          )}
        </Box>

        {/* Center / Search Input */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: '#1f2937',
            borderRadius: 1,
            px: 1,
            py: 0.25,
            border: '1px solid #374151',
            minWidth: 160,
            maxWidth: 240,
          }}
        >
          <SearchIcon sx={{ color: '#9ca3af', fontSize: '1rem', mr: 0.5 }} />
          <InputBase
            placeholder="Find in text..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              color: '#ffffff',
              fontSize: '0.8rem',
              width: '100%',
              '& input': { padding: 0 },
            }}
          />
          {searchQuery && (
            <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ color: '#9ca3af', p: 0.25 }}>
              <CloseIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          )}
        </Box>

        {/* Right Toolbar Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Encoding selector */}
          <Select
            size="small"
            value={encoding}
            onChange={(e) => handleEncodingChange(e.target.value)}
            sx={{
              color: '#f3f4f6',
              bgcolor: '#1f2937',
              fontSize: '0.75rem',
              height: 28,
              '& .MuiSelect-select': { py: 0.5, px: 1 },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#4b5563' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9ca3af' },
            }}
          >
            {ENCODING_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.8rem' }}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, bgcolor: '#4b5563' }} />

          {/* Line Numbers Toggle */}
          <Tooltip title={showLineNumbers ? 'Hide Line Numbers' : 'Show Line Numbers'}>
            <IconButton
              size="small"
              onClick={() => setShowLineNumbers((prev) => !prev)}
              sx={{ color: showLineNumbers ? '#60a5fa' : '#9ca3af' }}
            >
              <FormatListNumberedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Word Wrap Toggle */}
          <Tooltip title={wordWrap ? 'Disable Word Wrap' : 'Enable Word Wrap'}>
            <IconButton
              size="small"
              onClick={() => setWordWrap((prev) => !prev)}
              sx={{ color: wordWrap ? '#60a5fa' : '#9ca3af' }}
            >
              <WrapTextIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Theme Toggle */}
          <Tooltip title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
            <IconButton
              size="small"
              onClick={() => setIsDarkMode((prev) => !prev)}
              sx={{ color: '#e5e7eb' }}
            >
              {isDarkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, bgcolor: '#4b5563' }} />

          {/* Zoom Out */}
          <Tooltip title="Decrease Font Size">
            <span>
              <IconButton size="small" onClick={handleZoomOut} disabled={fontSize <= 10} sx={{ color: '#e5e7eb' }}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Typography
            variant="caption"
            sx={{
              minWidth: '36px',
              textAlign: 'center',
              userSelect: 'none',
              color: '#f3f4f6',
              fontFamily: 'monospace',
            }}
          >
            {fontSize}px
          </Typography>

          {/* Zoom In */}
          <Tooltip title="Increase Font Size">
            <span>
              <IconButton size="small" onClick={handleZoomIn} disabled={fontSize >= 26} sx={{ color: '#e5e7eb' }}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          {/* Reset Zoom */}
          <Tooltip title="Reset Font Size">
            <IconButton size="small" onClick={handleZoomReset} sx={{ color: '#e5e7eb' }}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, bgcolor: '#4b5563' }} />

          {/* Copy */}
          <Tooltip title={copied ? 'Copied to Clipboard!' : 'Copy All Text'}>
            <IconButton size="small" onClick={handleCopy} sx={{ color: copied ? '#4ade80' : '#e5e7eb' }}>
              {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          {/* Print */}
          <Tooltip title="Print Document">
            <IconButton size="small" onClick={handlePrint} sx={{ color: '#e5e7eb' }}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Download */}
          {canDownload && (
            <Tooltip title="Download Original File">
              <IconButton size="small" onClick={handleDownload} sx={{ color: '#60a5fa' }}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* Reload */}
          <Tooltip title="Reload">
            <IconButton size="small" onClick={loadFile} sx={{ color: '#e5e7eb' }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Main Text Content Area */}
      <Box
        ref={textContainerRef}
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          position: 'relative',
          display: 'flex',
          bgcolor: bgTheme,
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', m: 'auto', p: 4 }}>
            <CircularProgress size={48} sx={{ color: '#60a5fa', mb: 2 }} />
            <Typography variant="body1" sx={{ color: '#9ca3af' }}>
              Loading text file...
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ maxWidth: 540, mx: 'auto', my: 'auto', p: 3 }}>
            <Alert
              severity="error"
              action={
                canDownload ? (
                  <Button color="inherit" size="small" startIcon={<DownloadIcon />} onClick={handleDownload}>
                    Download
                  </Button>
                ) : null
              }
            >
              {error.message || 'Failed to read text file'}
            </Alert>
          </Box>
        )}

        {!loading && !error && (
          <Box
            sx={{
              display: 'flex',
              minWidth: '100%',
              fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', 'Courier New', monospace",
              fontSize: `${fontSize}px`,
              lineHeight: 1.6,
              tabSize: 4,
            }}
          >
            {/* Line Numbers Gutter */}
            {showLineNumbers && (
              <Box
                sx={{
                  userSelect: 'none',
                  textAlign: 'right',
                  pr: 1.5,
                  pl: 2,
                  py: 2,
                  bgcolor: gutterBg,
                  color: gutterColor,
                  borderRight: `1px solid ${borderColor}`,
                  flexShrink: 0,
                  fontSize: `${fontSize * 0.9}px`,
                }}
              >
                {lines.map((_, i) => (
                  <div key={i} style={{ height: `${fontSize * 1.6}px` }}>
                    {i + 1}
                  </div>
                ))}
              </Box>
            )}

            {/* Text Lines Area */}
            <Box
              sx={{
                flexGrow: 1,
                py: 2,
                px: 2.5,
                whiteSpace: wordWrap ? 'pre-wrap' : 'pre',
                wordBreak: wordWrap ? 'break-word' : 'normal',
                overflowX: wordWrap ? 'hidden' : 'auto',
                color: textTheme,
              }}
            >
              {lines.map((lineContent, idx) => {
                if (searchQuery && lineContent.toLowerCase().includes(searchQuery.toLowerCase())) {
                  const parts = lineContent.split(new RegExp(`(${searchQuery.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')})`, 'gi'));
                  return (
                    <div key={idx} style={{ height: wordWrap ? 'auto' : `${fontSize * 1.6}px`, minHeight: `${fontSize * 1.6}px` }}>
                      {parts.map((p, pIdx) =>
                        p.toLowerCase() === searchQuery.toLowerCase() ? (
                          <mark
                            key={pIdx}
                            style={{
                              backgroundColor: searchHighlightBg,
                              color: searchHighlightColor,
                              padding: '1px 2px',
                              borderRadius: '2px',
                            }}
                          >
                            {p}
                          </mark>
                        ) : (
                          p
                        )
                      )}
                    </div>
                  );
                }

                return (
                  <div key={idx} style={{ height: wordWrap ? 'auto' : `${fontSize * 1.6}px`, minHeight: `${fontSize * 1.6}px` }}>
                    {lineContent || '\u00A0'}
                  </div>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

TextViewer.propTypes = {
  fileUrl: PropTypes.string.isRequired,
  filename: PropTypes.string,
  canDownload: PropTypes.bool,
};

TextViewer.defaultProps = {
  filename: '',
  canDownload: true,
};
